import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import PageNavigation from '../../../components/PageNavigation/PageNavigation'
import './VSCodeExtension.css'

const VSCodeExtension = () => {
  const [activeSection, setActiveSection] = useState('overview')
  const [lightboxImage, setLightboxImage] = useState(null)
  const [lightboxAlt, setLightboxAlt] = useState('')

  const openLightbox = (imageSrc, altText) => {
    setLightboxImage(imageSrc)
    setLightboxAlt(altText)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setLightboxImage(null)
    setLightboxAlt('')
    document.body.style.overflow = ''
  }

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && lightboxImage) {
        closeLightbox()
      }
    }
    if (lightboxImage) {
      window.addEventListener('keydown', handleEscape)
    }
    return () => {
      window.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [lightboxImage])

  const sections = [
    { id: 'overview', title: 'Overview' },
    { id: 'installation', title: 'Installation' },
    { id: 'setup', title: 'Setup & Configuration' },
    { id: 'deployment', title: 'Deployment Features' },
    { id: 'database', title: 'Database Management' },
    { id: 'features', title: 'Features' },
    { id: 'commands', title: 'Commands' }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(`extension-${sections[i].id}`)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  return (
    <div className="vscode-extension-page">
      <div className="vscode-extension-content-wrapper">
        <div className="vscode-extension-main-content">
          <h1>VS Code Extension</h1>
          <p className="intro">
            The <strong>Aedify</strong> extension allows you to deploy applications and manage databases 
            directly from VS Code or Cursor. Deploy your projects with one click, manage databases, view deployments, 
            and access all Aedify features without leaving your editor.
          </p>

          <section id="extension-overview" className="vscode-extension-section">
            <h2>Overview</h2>
            <p>
              The Aedify Deploy extension provides a comprehensive solution for cloud deployment and database 
              management from within your code editor. Whether you're using VS Code or Cursor, you can:
            </p>
            <ul>
              <li><strong>Deploy Applications:</strong> One-click deployment with automatic framework detection</li>
              <li><strong>Manage Databases:</strong> Create, view, and manage PostgreSQL, MySQL, MongoDB, and Redis databases</li>
              <li><strong>Monitor Deployments:</strong> View deployment status and logs directly in VS Code</li>
              <li><strong>Secure by Default:</strong> Automatic secret scanning before upload</li>
            </ul>
            <p>
              The extension features a sidebar panel with quick actions, status bar integration, and a webview-based 
              database management interface. All operations are authenticated using secure API keys stored in your 
              system's keychain.
            </p>
          </section>

          <section id="extension-installation" className="vscode-extension-section">
            <h2>Installation</h2>
            <p>
              The installation process differs slightly depending on whether you're using VS Code or Cursor. 
              Follow the instructions below for your editor.
            </p>

            <h3>Installing in VS Code</h3>
            <p>
              VS Code users can install the extension directly from the marketplace:
            </p>
            <ol>
              <li>Open VS Code</li>
              <li>Click on the <strong>Extensions</strong> icon in the sidebar (or press <code>Cmd+Shift+X</code> on Mac / <code>Ctrl+Shift+X</code> on Windows/Linux)</li>
              <li>In the search box, type <strong>"aedify"</strong></li>
              <li>Find <strong>"Aedify"</strong> in the results</li>
              <li>Click <strong>Install</strong></li>
            </ol>
            <p>
              The extension will be installed and ready to use immediately. You'll see the Aedify icon in the 
              activity bar on the left side of VS Code.
            </p>

            <h3>Installing in Cursor</h3>
            <p>
              Cursor users can install the extension by downloading the VSIX file from GitHub releases:
            </p>
            <ol>
              <li>Visit <a href="https://github.com/aedify-ai/Aedify-extension/releases/latest" target="_blank" rel="noopener noreferrer">GitHub Releases</a> to download the latest <code>.vsix</code> file</li>
              <li>Open Cursor</li>
              <li>Press <code>Cmd+Shift+P</code> (Mac) or <code>Ctrl+Shift+P</code> (Windows/Linux) to open the command palette</li>
              <li>Type <strong>"Extensions: Install from VSIX..."</strong> and select it</li>
              <li>Select the downloaded <code>.vsix</code> file to install</li>
            </ol>
            <p>
              Alternatively, you can try searching for the extension in Cursor's extension marketplace:
            </p>
            <ol>
              <li>Press <code>Cmd+Shift+P</code> (Mac) or <code>Ctrl+Shift+P</code> (Windows/Linux)</li>
              <li>Type <strong>"Extensions: Install Extensions"</strong> and select it</li>
              <li>In the search box, type <strong>"aedify"</strong></li>
              <li>If available, find <strong>"Aedify"</strong> and click <strong>Install</strong></li>
            </ol>

            <p>
              After successful installation, you will see the <strong>Aedify</strong> icon appear in the activity bar at the bottom-left corner of your editor, as shown below:
            </p>
            <div className="screenshot-container">
              <img
                src="/deploy-icon.png"
                alt="Aedify Deploy icon in the activity bar"
                className="extension-screenshot"
                onClick={() => openLightbox('/deploy-icon.png', 'Aedify Deploy icon in the activity bar')}
                style={{ cursor: 'pointer', maxWidth: '100%', borderRadius: '8px', border: '1px solid var(--border-color, #333)' }}
              />
              <p className="screenshot-caption">The Aedify icon (highlighted) appears in the activity bar after installation</p>
            </div>

            <div className="important-notice">
              <div className="notice-header">
                <span className="notice-label">NOTE</span>
              </div>
              <p>
                After installation, you may need to reload the editor window for the extension to be fully activated. 
                You can do this by restarting the editor or using the reload command. The extension requires VS Code 
                version 1.85.0 or higher.
              </p>
            </div>
          </section>

          <section id="extension-setup" className="vscode-extension-section">
            <h2>Setup & Configuration</h2>
            <p>
              Before you can use the extension, you need to authenticate with your Aedify account using an API key. 
              This key authenticates all requests to the Aedify API.
            </p>

            <h3>Step 1: Get Your API Key</h3>
            <p>
              If you don't have an API key yet, create one from the Aedify Console. For detailed instructions on creating and managing API keys, see our <Link to="/resources/api-keys">API Keys documentation</Link>.
            </p>
            <p>
              Quick steps:
            </p>
            <ol>
              <li>Go to <a href="https://aedify.ai/console/api-keys" target="_blank" rel="noopener noreferrer">Aedify Console → API Keys</a></li>
              <li>Click <strong>"Create API Key"</strong></li>
              <li>Copy the generated key immediately (it's only shown once)</li>
              <li>Store it securely in a password manager</li>
            </ol>

            <div className="important-warning">
              <p>
                <strong>Important:</strong> API keys are displayed only once when created. Make sure to copy and 
                store your key securely before closing the dialog. Lost keys must be revoked and recreated.
              </p>
            </div>

            <h3>Step 2: Sign In to the Extension</h3>
            <p>
              Authenticate the extension with your API key:
            </p>
            <ol>
              <li>Click the <strong>Aedify</strong> icon in the activity bar (left sidebar)</li>
              <li>In the Quick Actions panel, click <strong>"Sign In with API Key"</strong></li>
              <li>Alternatively, open the command palette (<code>Cmd+Shift+P</code> / <code>Ctrl+Shift+P</code>) and type <strong>"Aedify: Sign In with API Key"</strong></li>
              <li>Paste your API key when prompted</li>
              <li>The key will be securely stored in your system's keychain</li>
            </ol>
            <p>
              Once signed in, you'll see your status in the status bar at the bottom of VS Code, and the Quick Actions 
              panel will show additional options like "Deploy Current Workspace" and "Open Database Manager".
            </p>

            <h3>Step 3: Configure Extension Settings (Optional)</h3>
            <p>
              The extension works with default settings, but you can customize them:
            </p>
            <ol>
              <li>Open VS Code/Cursor Settings (<code>Cmd+,</code> / <code>Ctrl+,</code>)</li>
              <li>Search for <strong>"aedify"</strong></li>
              <li>Configure the following settings as needed:</li>
            </ol>

            <div className="command-item">
              <h4>Available Settings</h4>
              <ul>
                <li><strong><code>aedify.apiUrl</code></strong> - Aedify API base URL (default: <code>https://deployment.aedify.ai</code>)</li>
                <li><strong><code>aedify.consoleUrl</code></strong> - Aedify Console URL (default: <code>https://aedify.ai/console</code>)</li>
                <li><strong><code>aedify.autoDetectFramework</code></strong> - Automatically detect project framework (default: <code>true</code>)</li>
                <li><strong><code>aedify.defaultRegion</code></strong> - Default deployment region (default: <code>us-west-2</code>)</li>
                <li><strong><code>aedify.maxUploadSize</code></strong> - Maximum upload size in MB (default: <code>250</code>)</li>
                <li><strong><code>aedify.scanForSecrets</code></strong> - Scan files for secrets before upload (default: <code>true</code>)</li>
                <li><strong><code>aedify.apiBaseUrl</code></strong> - Base URL for Aedify Database API (default: <code>https://deployment.aedify.ai</code>)</li>
              </ul>
            </div>
          </section>

          <section id="extension-deployment" className="vscode-extension-section">
            <h2>Deployment Features</h2>
            <p>
              The extension makes deploying your applications to Aedify incredibly simple with one-click deployment 
              and automatic framework detection.
            </p>

            <h3>Quick Deploy</h3>
            <p>
              The fastest way to deploy your project:
            </p>
            <ol>
              <li>Open your project workspace in VS Code/Cursor</li>
              <li>Press <code>Cmd+Shift+D</code> (Mac) or <code>Ctrl+Shift+D</code> (Windows/Linux)</li>
              <li>Or click the <strong>Aedify</strong> status bar item at the bottom</li>
              <li>Confirm the deployment when prompted</li>
            </ol>
            <p>
              The extension will automatically:
            </p>
            <ul>
              <li>Detect your project framework (Next.js, React, Vue, Express, Python, etc.)</li>
              <li>Pack your files (excluding <code>node_modules</code>, <code>.git</code>, and other ignored files)</li>
              <li>Scan for potential secrets before upload</li>
              <li>Upload and build your project</li>
              <li>Deploy to Aedify's cloud infrastructure</li>
            </ul>

            <h3>Deploy Wizard</h3>
            <p>
              For more control over deployment settings, use the deploy wizard:
            </p>
            <ol>
              <li>Open the command palette (<code>Cmd+Shift+P</code> / <code>Ctrl+Shift+P</code>)</li>
              <li>Type <strong>"Aedify: Deploy to Aedify"</strong> and select it</li>
              <li>Enter your project name when prompted</li>
              <li>The extension will guide you through the deployment process</li>
            </ol>

            <h3>Project Configuration</h3>
            <p>
              For advanced configuration, create an <code>aedify.json</code> file in your project root:
            </p>
            <pre><code>{`{
  "name": "my-project",
  "framework": "nextjs",
  "build": {
    "command": "npm run build"
  },
  "runtime": {
    "port": 3000
  },
  "resources": {
    "cpu": "0.5",
    "memory": "512m"
  },
  "env": {
    "NODE_ENV": "production"
  }
}`}</code></pre>

            <h3>Secret Scanning</h3>
            <p>
              Before uploading your files, the extension automatically scans for potential secrets including:
            </p>
            <ul>
              <li>API keys (AWS, GitHub, Google, Stripe, etc.)</li>
              <li>Private keys (RSA, DSA, EC, OpenSSH)</li>
              <li>OAuth tokens</li>
              <li>Generic secrets and credentials</li>
            </ul>
            <p>
              If secrets are detected, you'll be warned before deployment proceeds. Sensitive files like 
              <code>.env</code>, <code>*.pem</code>, and <code>*.key</code> are automatically excluded from uploads.
            </p>

            <h3>Ignore Patterns</h3>
            <p>
              The extension respects these ignore files (in priority order):
            </p>
            <ol>
              <li><code>.deployignore</code> - Deployment-specific ignore patterns</li>
              <li><code>.dockerignore</code> - Docker ignore patterns</li>
              <li><code>.gitignore</code> - Git ignore patterns</li>
            </ol>
            <p>
              By default, the following are excluded: <code>node_modules</code>, <code>.git</code>, <code>.env</code>, 
              <code>dist</code>, <code>build</code>, <code>.next</code>, <code>__pycache__</code>, and more.
            </p>
          </section>

          <section id="extension-database" className="vscode-extension-section">
            <h2>Database Management</h2>
            <p>
              Manage your Aedify databases directly from VS Code with a comprehensive database management interface.
            </p>

            <h3>Opening the Database Manager</h3>
            <p>
              Access the database management interface:
            </p>
            <ol>
              <li>Click the <strong>Aedify</strong> icon in the activity bar</li>
              <li>In the Quick Actions panel, click <strong>"Open Database Manager"</strong></li>
              <li>Or use the command palette: <strong>"Aedify: Open Database Manager"</strong></li>
            </ol>

            <h3>Creating a Database</h3>
            <p>
              Create a new database quickly:
            </p>
            <ol>
              <li>Open the command palette (<code>Cmd+Shift+P</code> / <code>Ctrl+Shift+P</code>)</li>
              <li>Type <strong>"Aedify: Create Database"</strong> and select it</li>
              <li>Follow the prompts to configure:</li>
              <ul>
                <li>Database name</li>
                <li>Engine (PostgreSQL, MySQL, MongoDB, Redis)</li>
                <li>Engine version</li>
                <li>Tier (CPU and memory allocation)</li>
                <li>Storage size</li>
              </ul>
              <li>Review pricing and confirm creation</li>
            </ol>

            <p>
              For detailed instructions, see our <Link to="/platform-capabilities/database">Database documentation</Link>.
            </p>

            <h3>Viewing Database Details</h3>
            <p>
              In the Database Manager webview, you can:
            </p>
            <ul>
              <li>View all your databases in a list</li>
              <li>See database status, resources, and creation date</li>
              <li>Access connection details (host, port, username, password)</li>
              <li>Copy connection strings with one click</li>
              <li>Manage database lifecycle (start, stop, restart, delete)</li>
              <li>Create and view backups</li>
            </ul>

            <h3>Listing Databases</h3>
            <p>
              Quickly list all databases in the output channel:
            </p>
            <ol>
              <li>Open the command palette</li>
              <li>Type <strong>"Aedify: List Databases"</strong> and select it</li>
              <li>View the list in the "Aedify Databases" output channel</li>
            </ol>
          </section>

          <section id="extension-features" className="vscode-extension-section">
            <h2>Features</h2>
            <p>
              The Aedify extension provides comprehensive cloud deployment and database management capabilities:
            </p>

            <h3>Deployment</h3>
            <ul>
              <li><strong>One-Click Deploy:</strong> Deploy with <code>Cmd+Shift+D</code> or via the status bar</li>
              <li><strong>Smart Packing:</strong> Automatically excludes unnecessary files and scans for secrets before upload</li>
              <li><strong>Quick Access:</strong> Open deployed sites directly from VS Code</li>
              <li>For detailed information, see our <Link to="/platform-capabilities/apps">Apps documentation</Link>.</li>
            </ul>

            <h3>Database Management</h3>
            <ul>
              <li><strong>Create & Manage:</strong> Create PostgreSQL, MySQL, MongoDB, or Redis databases with customizable resources</li>
              <li><strong>Connection Details:</strong> View and copy connection strings, credentials, and connection information</li>
              <li><strong>Lifecycle Control:</strong> Start, stop, restart, and delete databases</li>
              <li>For detailed information, see our <Link to="/platform-capabilities/database">Database documentation</Link>.</li>
            </ul>

            <h3>Security</h3>
            <ul>
              <li><strong>Secure Credential Storage:</strong> API keys stored in OS keychain, never in plaintext</li>
              <li><strong>Secret Detection:</strong> Scans files for secrets before upload with warnings</li>
              <li><strong>Automatic Exclusion:</strong> Blocks sensitive files (<code>.env</code>, <code>*.pem</code>, <code>*.key</code>) from uploads</li>
              <li><strong>Sandboxed Communication:</strong> All API requests routed through the extension host</li>
            </ul>
          </section>

          <section id="extension-commands" className="vscode-extension-section">
            <h2>Commands</h2>
            <p>
              The extension provides several commands accessible via the command palette. Open the command palette 
              with <code>Cmd+Shift+P</code> (Mac) or <code>Ctrl+Shift+P</code> (Windows/Linux), then type 
              <strong>"Aedify"</strong> to see all available commands.
            </p>

            <h3>Deployment Commands</h3>

            <div className="command-item">
              <h4>Aedify: Deploy Current Workspace</h4>
              <p>
                <strong>Shortcut:</strong> <code>Cmd+Shift+D</code> (Mac) / <code>Ctrl+Shift+D</code> (Windows/Linux)
              </p>
              <p>
                Quick deploy your current workspace to Aedify. The extension will automatically detect your framework, 
                pack your files, scan for secrets, and deploy. This is the fastest way to deploy.
              </p>
            </div>

            <div className="command-item">
              <h4>Aedify: Deploy to Aedify</h4>
              <p>
                Opens the deployment wizard for more control over deployment settings. You'll be prompted to enter a 
                project name and can configure deployment options.
              </p>
            </div>

            <div className="command-item">
              <h4>Aedify: View Deployments</h4>
              <p>
                Opens the deployments view in the sidebar to see all your deployments and their status.
              </p>
            </div>

            <h3>Authentication Commands</h3>

            <div className="command-item">
              <h4>Aedify: Sign In with API Key</h4>
              <p>
                Authenticate the extension with your Aedify API key. The key will be securely stored in your 
                system's keychain.
              </p>
            </div>

            <div className="command-item">
              <h4>Aedify: Sign Out</h4>
              <p>
                Sign out of your Aedify account and clear stored credentials.
              </p>
            </div>

            <h3>Database Commands</h3>

            <div className="command-item">
              <h4>Aedify: Open Database Manager</h4>
              <p>
                Opens the main database management webview. This is the primary interface for managing your databases. 
                You can view all databases, create new ones, view details, manage backups, and control database lifecycle.
              </p>
            </div>

            <div className="command-item">
              <h4>Aedify: Create Database</h4>
              <p>
                Quick-create a new database via the command palette. You'll be prompted to select:
              </p>
              <ul>
                <li>Database name</li>
                <li>Database engine (PostgreSQL, MySQL, MongoDB, Redis)</li>
                <li>Engine version</li>
                <li>Tier (CPU and memory allocation)</li>
                <li>Storage size</li>
              </ul>
              <p>
                The command shows pricing information and total monthly cost before creation.
              </p>
            </div>

            <div className="command-item">
              <h4>Aedify: List Databases</h4>
              <p>
                Lists all your databases in the VS Code output channel. Shows database name, engine, version, 
                status, and resource allocation (CPU, memory, storage). Useful for quick reference without opening 
                the full UI.
              </p>
            </div>

            <h3>Utility Commands</h3>

            <div className="command-item">
              <h4>Aedify: Open Aedify Console</h4>
              <p>
                Opens the Aedify web console in your default browser. Useful for accessing features not yet 
                available in the extension.
              </p>
            </div>

            <div className="command-item">
              <h4>Aedify: Refresh Deployments</h4>
              <p>
                Refreshes the deployments view to show the latest status of your deployments.
              </p>
            </div>

            <h3>Keyboard Shortcuts</h3>
            <p>
              The extension defines one default keyboard shortcut:
            </p>
            <ul>
              <li><strong><code>Cmd+Shift+D</code></strong> (Mac) / <strong><code>Ctrl+Shift+D</code></strong> (Windows/Linux) - Deploy Current Workspace</li>
            </ul>
            <p>
              You can add custom shortcuts for other commands in VS Code/Cursor settings:
            </p>
            <ol>
              <li>Open Keyboard Shortcuts (<code>Cmd+K Cmd+S</code> / <code>Ctrl+K Ctrl+S</code>)</li>
              <li>Search for <strong>"Aedify"</strong></li>
              <li>Click the pencil icon next to any command</li>
              <li>Press your desired key combination</li>
            </ol>
          </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Section Navigation */}
        <aside className="vscode-extension-sidebar">
          <div className="sidebar-section">
            <h3>Contents</h3>
            <nav className="section-nav">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#extension-${section.id}`}
                  className={`section-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveSection(section.id)
                    document.getElementById(`extension-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>

          <div className="sidebar-section tips-section">
            <h3>Quick Tips</h3>
            <ul className="tips-list">
              <li>Use <code>Cmd+Shift+D</code> for fastest deployment</li>
              <li>Check the status bar for quick deploy access</li>
              <li>API keys are stored securely—never share them</li>
              <li>Connection strings can be copied with one click</li>
              <li>Passwords are hidden by default for security</li>
              <li>Create an <code>aedify.json</code> for advanced configuration</li>
              <li>Use the sidebar Quick Actions for common tasks</li>
            </ul>
          </div>
        </aside>
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close lightbox">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <img src={lightboxImage} alt={lightboxAlt} className="lightbox-image" />
          </div>
        </div>
      )}
    </div>
  )
}

export default VSCodeExtension
